import { useCallback, useState } from 'react';
import { sanitizers } from '../../../utils/validation';

export const MAX_GENRE_LENGTH = 120;
const MAX_GENRE_COUNT = 3;

/**
 * State + handlers for the "Add up to 3 tags" chip picker.
 *
 *   - Sanitizes on commit, trims whitespace, rejects duplicates and the
 *     empty string.
 *   - Enforces a per-tag length cap (`MAX_GENRE_LENGTH`) and surfaces
 *     overruns via the caller-provided `onOverflow` hook (used to raise
 *     an AlertDialog in PostForm).
 *   - Commits on both `click` (form-button submit) and Enter keystroke
 *     inside the query input.
 *   - `reset` clears both selections and the in-flight query, used after
 *     the surrounding form submits.
 */
const useGenreChips = ({ onOverflow, initial = [] } = {}) => {
  const [selectedGenres, setSelectedGenres] = useState(initial);
  const [genreQuery, setGenreQuery] = useState('');

  const handleGenreSelect = useCallback(
    (e) => {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      const newGenre = genreQuery.trim();
      const sanitizedGenre = sanitizers.text(newGenre);

      if (sanitizedGenre.length > MAX_GENRE_LENGTH) {
        if (onOverflow) onOverflow(MAX_GENRE_LENGTH);
        return;
      }

      if (
        sanitizedGenre &&
        selectedGenres.length < MAX_GENRE_COUNT &&
        !selectedGenres.includes(sanitizedGenre)
      ) {
        setSelectedGenres([...selectedGenres, sanitizedGenre]);
        setGenreQuery('');
      }
    },
    [genreQuery, selectedGenres, onOverflow]
  );

  const handleRemoveGenre = useCallback((genre) => {
    setSelectedGenres((prev) => prev.filter((g) => g !== genre));
  }, []);

  const handleGenreQueryChange = useCallback((e) => {
    setGenreQuery(e.target.value);
  }, []);

  const handleGenreKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === 'Return') {
        handleGenreSelect(e);
      }
    },
    [handleGenreSelect]
  );

  const reset = useCallback(() => {
    setSelectedGenres([]);
    setGenreQuery('');
  }, []);

  return {
    selectedGenres,
    setSelectedGenres,
    genreQuery,
    handleGenreSelect,
    handleRemoveGenre,
    handleGenreQueryChange,
    handleGenreKeyDown,
    reset,
    maxGenreCount: MAX_GENRE_COUNT,
  };
};

export default useGenreChips;
